import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faMapMarkerAlt, faLock } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const footerLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Vision & Mission', path: '/vision-mission' },
    { name: 'Services', path: '/services' },
    { name: 'Our Clients', path: '/clients' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <footer className="relative bg-secondary text-white pt-16 pb-8 overflow-hidden">
      {/* World Map Background */}
      <div className="absolute inset-0 z-0 opacity-10 pointer-events-none overflow-hidden">
        <motion.div
          initial={{ scale: 1 }}
          animate={{ 
            scale: 1.05,
            x: [0, 5, 0, -5, 0],
            y: [0, -5, 0, 5, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="w-full h-full"
        >
          <img 
            src="/images/worldmap-dots.svg" 
            alt="World Map" 
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
      </div>

      <div className="container relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Company Information */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-xl font-bold text-white mb-6">Skill Point Consultants</h3>
            <p className="mb-6 text-gray-300">
              Your Partner in Professional Growth. We help professionals and companies find the ideal fit by bridging the gap between talent and success.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.instagram.com/skillpoint_consultants/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-accent transition-colors duration-300"
                aria-label="Instagram"
              >
                <FontAwesomeIcon icon={faInstagram} size="lg" />
              </a>
              <a 
                href="https://www.linkedin.com/company/skill-point-consultants/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-accent transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <FontAwesomeIcon icon={faLinkedin} size="lg" />
              </a>
            </div>
            
            {/* Security Badge */}
            <div className="mt-6 flex items-center">
              <div className="mr-2 bg-green-600 text-white p-1 rounded">
                <FontAwesomeIcon icon={faLock} size="sm" />
              </div>
              <span className="text-gray-300 text-sm">Secure HTTPS Connection</span>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-xl font-bold text-white mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-white hover:pl-2 transition-all duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-xl font-bold text-white mb-6">Contact Us</h3>
            <div className="space-y-4">
              <p className="flex items-start">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-3 mt-1 text-accent" />
                <span className="text-gray-300">
                  Sector -90, Gurugram, Haryana, 122505
                </span>
              </p>
              <p className="flex items-center">
                <FontAwesomeIcon icon={faPhone} className="mr-3 text-accent" />
                <span className="text-gray-300">+91 82955 31760, +91 9991818428</span>
              </p>
              <p className="flex items-center">
                <FontAwesomeIcon icon={faEnvelope} className="mr-3 text-accent" />
                <a
                  href="mailto:business@skillpointconsultants.com"
                  className="text-gray-300 hover:text-white transition-colors duration-300"
                >
                  business@skillpointconsultants.com
                </a>
              </p>
            </div>
          </motion.div>
        </div>

        {/* Copyright and Legal Links */}
        <div className="pt-8 border-t border-gray-700 text-center text-gray-400">
          <div className="mb-4 flex justify-center space-x-4 text-sm">
            <Link to="/privacy-policy" className="hover:text-white transition-colors duration-300">
              Privacy Policy
            </Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-white transition-colors duration-300">
              Terms of Service
            </Link>
          </div>
          <p>&copy; {new Date().getFullYear()} Skill Point Consultants. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 